import { useState, useEffect } from "react";
import { useSchools } from "../../hooks/useSchools";
import { useAuthStore } from "../../store/authStore";
import { userAPI } from "../user/userAPI";

export default function AdminDashboard() {
  const { user, isAdmin } = useAuthStore();
  const { getPendingSchools, approveSchool, rejectSchool, loading } =
    useSchools();

  const [pendingSchools, setPendingSchools] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      if (isAdmin()) {
        await fetchDashboardData();
        await fetchPendingSchools();
      }
    };

    fetchData();
  }, [isAdmin]);

  const fetchDashboardData = async () => {
    try {
      const result = await userAPI.getDashboard();
      if (result.success) {
        setDashboardData(result.data);
      }
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
    }
  };

  const fetchPendingSchools = async () => {
    try {
      const result = await getPendingSchools();
      if (result.success !== false) {
        setPendingSchools(result.schools || result);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Đã xảy ra lỗi khi tải danh sách trường chờ duyệt");
    }
  };

  const handleApprove = async (schoolId) => {
    try {
      const result = await approveSchool(schoolId);
      if (result.success !== false) {
        setPendingSchools((prev) =>
          prev.filter((school) => school._id !== schoolId)
        );
        fetchDashboardData(); // Refresh dashboard stats
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Đã xảy ra lỗi khi duyệt trường");
    }
  };

  const handleReject = async () => {
    if (!selectedSchool || !rejectReason.trim()) return;

    try {
      const result = await rejectSchool(selectedSchool._id, rejectReason);
      if (result.success !== false) {
        setPendingSchools((prev) =>
          prev.filter((school) => school._id !== selectedSchool._id)
        );
        setShowRejectModal(false);
        setSelectedSchool(null);
        setRejectReason("");
        fetchDashboardData(); // Refresh dashboard stats
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Đã xảy ra lỗi khi từ chối trường");
    }
  };

  const openRejectModal = (school) => {
    setSelectedSchool(school);
    setShowRejectModal(true);
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setSelectedSchool(null);
    setRejectReason("");
  };

  if (!isAdmin()) {
    return (
      <div className="container">
        <div className="dashboard__access-denied">
          <h1>Truy cập bị từ chối</h1>
          <p>Bạn không có quyền truy cập trang này.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="dashboard">
        <div className="dashboard__header">
          <h1 className="dashboard__title">Dashboard Admin</h1>
          <p className="dashboard__subtitle">Quản lý hệ thống review trường</p>
        </div>

        {/* Dashboard Statistics */}
        {dashboardData && (
          <div className="dashboard__stats">
            <div className="dashboard__stat-card dashboard__stat-card--users">
              <div className="dashboard__stat-icon">
                <svg
                  className="icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                  />
                </svg>
              </div>
              <div className="dashboard__stat-info">
                <p className="dashboard__stat-label">Tổng người dùng</p>
                <p className="dashboard__stat-value">
                  {dashboardData.overview.totalUsers}
                </p>
              </div>
            </div>

            <div className="dashboard__stat-card dashboard__stat-card--schools">
              <div className="dashboard__stat-icon">
                <svg
                  className="icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <div className="dashboard__stat-info">
                <p className="dashboard__stat-label">Trường đã duyệt</p>
                <p className="dashboard__stat-value">
                  {dashboardData.overview.approvedSchools}
                </p>
              </div>
            </div>

            <div className="dashboard__stat-card dashboard__stat-card--pending">
              <div className="dashboard__stat-icon">
                <svg
                  className="icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="dashboard__stat-info">
                <p className="dashboard__stat-label">Trường chờ duyệt</p>
                <p className="dashboard__stat-value">
                  {dashboardData.overview.pendingSchools}
                </p>
              </div>
            </div>

            <div className="dashboard__stat-card dashboard__stat-card--reviews">
              <div className="dashboard__stat-icon">
                <svg
                  className="icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                  />
                </svg>
              </div>
              <div className="dashboard__stat-info">
                <p className="dashboard__stat-label">Tổng đánh giá</p>
                <p className="dashboard__stat-value">
                  {dashboardData.overview.totalReviews}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="dashboard__error">
            <p>{error}</p>
          </div>
        )}

        {/* Pending Schools */}
        <div className="dashboard__pending-section">
          <h2 className="dashboard__section-title">
            Trường chờ duyệt ({pendingSchools.length})
          </h2>

          {loading ? (
            <div className="dashboard__loading">
              <div className="loading-spinner"></div>
              <p>Đang tải...</p>
            </div>
          ) : pendingSchools.length === 0 ? (
            <div className="dashboard__empty">
              <p>Không có trường nào chờ duyệt</p>
            </div>
          ) : (
            <div className="dashboard__pending-list">
              {pendingSchools.map((school) => (
                <div key={school._id} className="dashboard__pending-item">
                  <div className="dashboard__pending-content">
                    <h3 className="dashboard__pending-name">{school.name}</h3>
                    {school.short_name && (
                      <p className="dashboard__pending-short-name">
                        ({school.short_name})
                      </p>
                    )}
                    {school.code_university && (
                      <p className="dashboard__pending-code">
                        Mã: {school.code_university}
                      </p>
                    )}
                    <p className="dashboard__pending-address">
                      {school.address}
                    </p>
                    <span className="dashboard__pending-level">
                      {school.level}
                    </span>

                    {school.description && (
                      <p className="dashboard__pending-description">
                        {school.description}
                      </p>
                    )}

                    <div className="dashboard__pending-meta">
                      <p>Được tạo bởi: {school.createdBy?.name || "N/A"}</p>
                      <p>
                        Ngày tạo:{" "}
                        {new Date(school.createdAt).toLocaleDateString("vi-VN")}
                      </p>
                    </div>
                  </div>

                  <div className="dashboard__pending-actions">
                    <button
                      onClick={() => handleApprove(school._id)}
                      className="btn btn--success btn--sm"
                    >
                      Duyệt
                    </button>
                    <button
                      onClick={() => openRejectModal(school)}
                      className="btn btn--danger btn--sm"
                    >
                      Từ chối
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reject Modal */}
        {showRejectModal && (
          <div className="modal-overlay">
            <div className="modal">
              <h3 className="modal__title">
                Từ chối trường: {selectedSchool?.name}
              </h3>

              <div className="modal__field">
                <label className="modal__label">Lý do từ chối *</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="modal__textarea"
                  placeholder="Nhập lý do từ chối..."
                />
              </div>

              <div className="modal__actions">
                <button
                  onClick={closeRejectModal}
                  className="btn btn--secondary"
                >
                  Hủy
                </button>
                <button
                  onClick={handleReject}
                  disabled={!rejectReason.trim()}
                  className="btn btn--danger"
                >
                  Xác nhận từ chối
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
